import React, { createContext, useContext, useState, useEffect } from 'react'
import { Auth0Client } from '@auth0/auth0-spa-js'
import config from 'src/auth_config.json'
import { setGetToken } from 'src/lib/apolloClient'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [auth0Client] = useState(() => {
    return new Auth0Client({
      domain: config.domain,
      client_id: config.clientId,
      redirect_uri: window.location.origin,
      cacheLocation: 'localstorage',
      audience: '/functions/graphql',
    })
  })

  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)

  const getToken = async () => {
    try {
      return await auth0Client.getTokenSilently()
    } catch (error) {
      console.error('Get token error:', error)
      return null
    }
  }

  useEffect(() => {
    // Set the getToken function for Apollo Client
    setGetToken(getToken)
  }, [])

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we're returning from a redirect
        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
          await auth0Client.handleRedirectCallback()
          window.history.replaceState({}, document.title, window.location.pathname)
        }

        const authenticated = await auth0Client.isAuthenticated()
        setIsAuthenticated(authenticated)

        if (authenticated) {
          const user = await auth0Client.getUser()
          setCurrentUser(user)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [auth0Client])

  const logIn = async (options = {}) => {
    try {
      await auth0Client.loginWithRedirect(options)
    } catch (error) {
      console.error('Login error:', error)
    }
  }

  const logOut = async (options = {}) => {
    try {
      await auth0Client.logout(options)
      setIsAuthenticated(false)
      setCurrentUser(null)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const value = {
    loading,
    isAuthenticated,
    currentUser,
    logIn,
    logOut,
    getToken,
    client: auth0Client,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

